import { useCallback, type RefCallback } from 'react';

/**
 * The reference design's scroll reveal, as a ref callback.
 *
 * `docs/design/stitch-reference.html` did this with an inline script that observed
 * every `.fade-up` element and added `.visible` when it entered the viewport. Same
 * behaviour here, with two differences that matter.
 *
 * First, the element is **visible until this hook runs**. The `.reveal` class — the
 * one that sets `opacity: 0` — is added by JavaScript, so a bundle error or an old
 * browser leaves a readable page rather than a column of invisible sections. The
 * reference had the opposite failure mode: its CSS hid the content and only the
 * script brought it back.
 *
 * Second, a `prefers-reduced-motion` reader never gets the class at all. The
 * stylesheet's reduced-motion block would collapse the transition to 0.01ms anyway,
 * but skipping the observer entirely avoids a frame of flicker.
 *
 * Returns a ref callback rather than a `useRef`, so the observer attaches exactly
 * when the node mounts and disconnects when it unmounts — no effect, no dependency
 * array to get wrong.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(): RefCallback<T> {
  return useCallback((node: T | null) => {
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    node.classList.add('reveal');

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          // One-shot: the element does not fade back out on the way past.
          observer.unobserve(entry.target);
        }
      },
      // Matches the reference script: fire once a tenth of the block is showing,
      // with the viewport's bottom edge pulled up 50px so the animation starts
      // fractionally before the element is properly on screen.
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      node.classList.remove('reveal', 'is-visible');
    };
  }, []);
}
